@let rows = downloads.entries();

@if (rows.length > 0)
{
  <!-- Named, so the list is findable in the accessibility tree, and named for what it actually holds:
       the downloads started from THIS tab. A second tab keeps its own register, and the browser's own
       download list is the only complete view (design D-11). -->
  <section class="downloadList" [attr.aria-label]="'itgen_gmr_downloads' | translate">
    <h2 class="downloadListHeading">
      <span translate="itgen_gmr_downloads"></span>
      <span class="downloadCount">{{ rows.length }}</span>
      <button type="button" class="downloadClear" (click)="downloads.clearFinished()" [attr.aria-label]="'itgen_gmr_download_clear' | translate" [title]="'itgen_gmr_download_clear' | translate">{{ 'itgen_gmr_download_clear' | translate }}</button>
    </h2>

    <!-- Why a submit produced no new download. Visible, because the same message written only into
         the live region below left everyone who is not using a screen reader looking at a button that
         appeared to do nothing. -->
    @if (downloads.notice())
    {
      <p class="downloadNotice">
        <span>{{ downloads.notice() }}</span>
        <button type="button" class="downloadAction downloadNoticeDismiss" (click)="downloads.dismissNotice()" [attr.aria-label]="'itgen_gmr_download_notice_dismiss' | translate" [title]="'itgen_gmr_download_notice_dismiss' | translate">&times;</button>
      </p>
    }

    <ul class="downloadRows">
      @for (row of rows; track row.id)
      {
        <li class="downloadRow" [class.highlighted]="row.id === downloads.highlightedId()" [attr.data-download-state]="row.state" [attr.data-download-preset]="row.presetName">
          <div class="downloadRowHead">
            <span class="downloadTitle" [translate]="row.titleCode"></span>
            <span class="downloadState">{{ stateText(row) }}</span>

            @if (canCancel(row))
            {
              <button type="button" class="downloadAction" (click)="downloads.cancel(row.id)" [attr.aria-label]="cancelLabel(row)" [title]="cancelLabel(row)">{{ 'itgen_gmr_download_cancel' | translate }}</button>
            }
            @if (canShowDetails(row))
            {
              <button type="button" class="downloadAction" (click)="downloads.showFailure(row.id)" [attr.aria-label]="detailsLabel(row)" [title]="detailsLabel(row)">{{ 'itgen_gmr_download_details' | translate }}</button>
            }
            @if (isTerminalState(row.state))
            {
              <button type="button" class="downloadAction downloadDismiss" (click)="downloads.remove(row.id)" [attr.aria-label]="dismissLabel(row)" [title]="dismissLabel(row)">&times;</button>
            }
          </div>

          <!-- Only where something is genuinely in progress. `waiting`, `uploading` and `cancelled`
               carry no progressbar at all: for `uploading` the browser is measuring the upload and
               this code is not, for `waiting` there is nothing under way, and a bar at zero beside a
               moving one reads as a download that has stalled (design S-21). -->
          @if (row.state === 'requested' || row.state === 'receiving')
          {
            <div class="downloadBar"
                 role="progressbar"
                 [attr.aria-label]="progressLabel(row)"
                 [attr.aria-valuemin]="row.totalBytes ? 0 : null"
                 [attr.aria-valuemax]="row.totalBytes ?? null"
                 [attr.aria-valuenow]="row.totalBytes ? row.receivedBytes : null"
                 [attr.aria-valuetext]="row.totalBytes ? progressText(row) : null">
              <div class="downloadBarFill" [class.indeterminate]="!row.totalBytes" [style.width.%]="percentage(row)"></div>
            </div>
          }

          <p class="downloadHint">{{ hintText(row) }}</p>

          <!-- Why this download ended up where it did, when the state alone does not say. Today that
               is the hand-over to the browser after the page could not fetch it. -->
          @if (row.note)
          {
            <p class="downloadNote">{{ row.note }}</p>
          }

          <!-- The serialised request. Untranslated on purpose: it is the literal form serialisation,
               the same string the bookmark hash carries, and it exists to be copied verbatim. It is
               also what tells two runs of one report apart. -->
          <p class="downloadRequest"><span class="downloadRequestLabel">Request</span><span>:</span> <span class="downloadRequestValue">{{ row.requestSummary }}</span></p>
        </li>
      }
    </ul>
  </section>
}

<!-- One polite live region for the whole list, carrying only transitions that ended something.
     Announcing `receiving`, or a byte count, would be continuous speech with three downloads
     running (design S-23). Outside the @if above so the region exists before the first
     announcement lands in it - a live region added and filled in the same turn is not reliably
     announced. -->
<p class="visuallyHidden" role="status" aria-live="polite">{{ downloads.announcement() }}</p>
